import { parse } from 'qs';

class GeoControlSearch {
  constructor(options = {}) {
    this.options = options;
    this.action = options.action;
    this.input = options.input;
    this.clearButton = options.clearButton;
    this.defaultList = options.defaultList;
    this.defaultResults = this.defaultList
      ? Array.from(this.defaultList.querySelectorAll('[data-search="list-item"]'))
      : [];
    this.resultsList = options.resultsList;
    this.results = this.defaultResults || [];
    this.activeIndex = -1;
    this.requestTimeout = null;
    this.attachListeners();
    this.keydownListener = this.keydownListener.bind(this);
    this.outerClickListener = this.outerClickListener.bind(this);
  }

  attachListeners() {
    this.input.addEventListener('focus', (event) => {
      this.input.select();
      if (this.options.parent) this.options.parent.classList.add('-focus');
      if (!this.input.value || this.input.value === this.input.getAttribute('data-current-geo')) {
        this.input.value = '';
        this.results = this.defaultResults;
        this.openDefaultList();
      }
      this.input.addEventListener('keydown', this.keydownListener);
      if (this.options.onFocus) this.options.onFocus(event);
    });

    this.input.addEventListener('input', (event) => {
      if (this.input.value) {
        if (this.clearButton) this.clearButton.classList.remove('hidden');
        this.selectResult(-1);
        this.sendRequest();
      } else {
        this.selectResult(-1);
        if (this.clearButton) this.clearButton.classList.add('hidden');
        this.results = this.defaultResults;
        this.openDefaultList();
      }
      if (this.options.onInput) this.options.onInput(event);
    });

    this.input.addEventListener('blur', (event) => {
      if (this.options.parent) this.options.parent.classList.remove('-focus');
      this.input.removeEventListener('keydown', this.keydownListener);
      if (this.options.onBlur) this.options.onBlur(event);
    });

    if (this.clearButton) {
      this.clearButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.reset();
      });
    }

    this.attachResultsHover();
    this.attachResultsClick();
  }

  sendRequest() {
    clearTimeout(this.requestTimeout);
    this.requestTimeout = setTimeout(() => {
      const q = this.input.value;
      const url = this.action;
      fetch(url, {
        method: 'POST',
        headers: {
          'Content-type': 'application/json',
          'X-CSRF-Token': window.csrf,
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          q,
          default_list: 'navigation',
          lang: window.currentLanguage,
          buildingType: window.params?.buildingType,
          type: window.params?.type,
          searchParams: parse(window.location.search, { ignoreQueryPrefix: true }),
        }),
      })
        .then((res) => res.text())
        .then((html) => {
          this.resultsList.innerHTML = html;
          this.highlightSearchQuery();
          this.results = [...this.resultsList.querySelectorAll('[data-search="list-item"]')];
          this.attachResultsHover();
          this.attachResultsClick();
          if (this.results.length > 0) {
            this.selectResult(0);
            this.openResultsList();
          } else {
            this.selectResult(-1);
            this.openDefaultList();
          }
        })
        .catch((error) => {
          console.warn(error);
        });
    }, 200);
  }

  attachResultsHover() {
    [].forEach.call(this.results, (result) => {
      if (result.dataset.hoverBound) return;
      result.dataset.hoverBound = '1';
      result.addEventListener('mouseenter', () => {
        this.selectResult(this.results.indexOf(result));
      });
    });
  }

  attachResultsClick() {
    [].forEach.call(this.results, (result) => {
      if (result.dataset.clickBound) return;
      result.dataset.clickBound = '1';
      result.addEventListener('click', (event) => {
        if (this.options.onSelect) {
          event.preventDefault();
          this.options.onSelect(this.results[this.activeIndex]);
          this.reset();
          if (this.options.close) {
            this.options.close();
          }
        }
      });
    });
  }

  updateConfig(config = {}) {
    Object.assign(this.options, config);
    this.action = this.options.action;
    this.clearButton = this.options.clearButton;
    this.defaultList = this.options.defaultList;
    this.defaultResults = this.defaultList
      ? Array.from(this.defaultList.querySelectorAll('[data-search="list-item"]'))
      : [];
    this.resultsList = this.options.resultsList;
    this.results = this.defaultResults;
    this.activeIndex = -1;
    this.attachResultsHover();
    this.attachResultsClick();
  }

  highlightSearchQuery() {
    const value = this.input.value;
    if (!value) return;
    const pattern = new RegExp(value, 'ig');
    const replacer = (str) => `<b>${str}</b>`;
    const highlightable = this.resultsList.querySelectorAll('[data-search="highlightable"]');
    [].forEach.call(highlightable, (item) => {
      item.innerHTML = item.innerHTML.replace(pattern, replacer);
    });
  }

  keydownListener(event) {
    if (event.keyCode === 27) {
      // esc
      this.reset();
    } else if (event.keyCode === 38) {
      // up
      if (this.results.length && this.activeIndex > 0) {
        this.selectResult(this.activeIndex - 1);
      }
    } else if (event.keyCode === 40) {
      // down
      if (this.results.length && this.activeIndex < this.results.length - 1) {
        this.selectResult(this.activeIndex + 1);
      }
    } else if (event.keyCode === 13) {
      // enter
      if (!this.results.length) return;
      event.preventDefault();
      const item = this.results[this.activeIndex];
      if (!item) return;
      if (this.options.onSelect) {
        this.options.onSelect(item);
        this.reset();
      } else {
        window.location.href = item.href;
      }
      if (this.options.close) {
        this.options.close();
      }
    }
  }

  selectResult(index) {
    this.activeIndex = index;
    [].forEach.call(this.results, (result, i) => {
      if (this.activeIndex === i) {
        result.classList.add('-active');
      } else {
        result.classList.remove('-active');
      }
    });
  }

  openDefaultList() {
    if (this.options.onOpenDefaultList) this.options.onOpenDefaultList();
    this.resultsList.classList.add('hidden');
    this.defaultList?.classList.remove('hidden');
  }

  openResultsList() {
    if (this.options.onOpenResultsList) this.options.onOpenResultsList();
    this.defaultList?.classList.add('hidden');
    this.resultsList.classList.remove('hidden');
  }

  reset() {
    this.selectResult(-1);
    if (this.clearButton) {
      this.input.value = '';
      this.clearButton.classList.add('hidden');
    }
  }

  outerClickListener(event) {
    const target = event.target;
    if (
      !this.input.contains(target) &&
      this.defaultList &&
      !this.defaultList.contains(target) &&
      !this.resultsList.contains(target)
    ) {
      this.reset();
    }
  }
}

export default GeoControlSearch;
